const SHUFFLE_TTL_MS = 3 * 24 * 60 * 60 * 1000;
const MAX_RECENTLY_VIEWED = 100;
const RECENTLY_VIEWED_KEY = "decorzoom_recently_viewed_products";

const slugify = (value) =>
  String(value || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const canUseLocalStorage = () => {
  try {
    return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
  } catch (error) {
    return false;
  }
};

const readJson = (key) => {
  if (!canUseLocalStorage()) return null;

  try {
    return JSON.parse(window.localStorage.getItem(key) || "null");
  } catch (error) {
    return null;
  }
};

const writeJson = (key, value) => {
  if (!canUseLocalStorage()) return;

  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    // Ignore storage quota/private-mode failures and keep the listing usable.
  }
};

const shuffleItems = (items) => {
  const shuffled = [...items];

  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  return shuffled;
};

export const getProductOrderId = (product) => {
  const stableId =
    product?.id ||
    product?.ID ||
    product?.packageGroup ||
    product?.slug ||
    product?.title;

  return slugify(stableId);
};

export const getShuffleOrderStorageKey = (pageSlug) =>
  `decorzoom_shuffle_order_${slugify(pageSlug) || "default"}`;

export const getSessionStableProductOrder = (products, pageSlug) => {
  const keyedProducts = products
    .map((product, index) => ({
      product,
      index,
      orderId: getProductOrderId(product),
    }))
    .filter((item) => item.orderId);

  const fallbackProducts = products.filter((product) => !getProductOrderId(product));
  const key = getShuffleOrderStorageKey(pageSlug);
  const cached = readJson(key);
  const now = Date.now();
  const currentIds = new Set(keyedProducts.map((item) => item.orderId));
  const productById = new Map(keyedProducts.map((item) => [item.orderId, item.product]));
  const isValidCache =
    cached &&
    Array.isArray(cached.order) &&
    Number.isFinite(cached.createdAt) &&
    now - cached.createdAt < SHUFFLE_TTL_MS;

  if (!isValidCache) {
    const shuffled = shuffleItems(keyedProducts);
    const nextOrder = shuffled.map((item) => item.orderId);

    writeJson(key, {
      createdAt: now,
      order: nextOrder,
    });

    return [...shuffled.map((item) => item.product), ...fallbackProducts];
  }

  const savedCurrentIds = cached.order.filter((orderId) => currentIds.has(orderId));
  const savedIdSet = new Set(savedCurrentIds);
  const newIds = keyedProducts
    .filter((item) => !savedIdSet.has(item.orderId))
    .map((item) => item.orderId);
  const nextOrder = [...newIds, ...savedCurrentIds];

  writeJson(key, {
    createdAt: cached.createdAt,
    order: nextOrder,
  });

  return [
    ...nextOrder.map((orderId) => productById.get(orderId)).filter(Boolean),
    ...fallbackProducts,
  ];
};

export const markProductRecentlyViewed = (product) => {
  const orderId = getProductOrderId(product);
  if (!orderId) return;

  const now = Date.now();
  const storedItems = readJson(RECENTLY_VIEWED_KEY);
  const existing = Array.isArray(storedItems) ? storedItems : [];
  const next = [
    {
      id: orderId,
      productId: product?.id || product?.ID || "",
      title: product?.title || "",
      slug: product?.slug || "",
      viewedAt: now,
    },
    ...existing.filter((item) => item?.id !== orderId),
  ].slice(0, MAX_RECENTLY_VIEWED);

  writeJson(RECENTLY_VIEWED_KEY, next);
};

export const getRecentlyViewedProductMap = () => {
  const storedItems = readJson(RECENTLY_VIEWED_KEY);
  const items = Array.isArray(storedItems) ? storedItems : [];

  return new Map(
    items
      .filter((item) => item?.id)
      .map((item) => [item.id, Number(item.viewedAt) || 0])
  );
};
